import React, { useState, useEffect } from 'react';
import { cn } from '@/utils/cn';
import { Contact } from '@/types/contact';

export interface TeeAvatarProps {
  /**
   * Avatar size
   * @default "40px"
   */
  size?:
    | '24px'
    | '32px'
    | '40px'
    | '44px'
    | '48px'
    | '56px'
    | '60px'
    | '70px'
    | '100px'
    | '128px';

  /**
   * Avatar style type
   * @default "Text"
   */
  style?: 'Empty State' | 'Text' | 'Image';

  /**
   * Avatar color variant/profile type
   * @default "Profile-1"
   */
  type?:
    | 'Default'
    | 'Profile-1'
    | 'Profile-2'
    | 'Profile-3'
    | 'Profile-4'
    | 'Profile-5'
    | 'Profile-6'
    | 'Profile-7'
    | 'Profile-Blue'
    | 'Gold'
    | 'Forest';

  /**
   * Avatar initials or text to display
   * @default "AB"
   */
  initials?: string;

  /**
   * Image source URL
   */
  src?: string;

  /**
   * Contact object for avatar
   */
  contact?: Contact;

  /**
   * Alt text for image
   */
  alt?: string;

  /**
   * Additional CSS classes
   */
  className?: string;
}

// Color variants mapping
const colorVariants: Record<string, string> = {
  'Profile-1': 'bg-[#C8E6C9]', // Light green
  'Profile-2': 'bg-gradient-to-b from-[#9FFAF0] to-[#9EEDFE]', // Gradient cyan to light cyan
  'Profile-3': 'bg-[#81C784]', // Green
  'Profile-4': 'bg-[#66BB6A]', // Dark green
  'Profile-5': 'bg-[#4DB8FF]', // Light cyan
  'Profile-6': 'bg-gradient-to-b from-[#FACB25] to-[#FE772D]', // Gradient yellow to orange
  'Profile-7': 'bg-[#1E88E5]', // Blue
  'Profile-Blue': 'bg-[#1565C0]', // Dark blue
  Gold: 'bg-[var(--color-gold-500)]', // Brand gold
  Forest: 'bg-[var(--color-forest)]', // Brand forest green
  Default: 'bg-[#E0E0E0]', // Gray
};

// Text color variants
const textColorVariants: Record<string, string> = {
  'Profile-1': 'text-[#1B5E20]', // Dark green text
  'Profile-2': 'text-[#2E7D32]', // Dark green text
  'Profile-3': 'text-[#388E3C]', // Dark green text
  'Profile-4': 'text-white', // White text
  'Profile-5': 'text-white', // White text
  'Profile-6': 'text-white', // White text
  'Profile-7': 'text-white', // White text
  'Profile-Blue': 'text-white', // White text
  Gold: 'text-[var(--color-forest)]', // Forest text on gold (contrast)
  Forest: 'text-[var(--text-on-forest)]', // Cream text on forest
  Default: 'text-[#616161]', // Gray text
};

// Size mapping
const sizeMap: Record<string, string> = {
  '24px': 'h-6 w-6',
  '32px': 'h-8 w-8',
  '40px': 'h-10 w-10',
  '44px': 'h-11 w-11',
  '48px': 'h-12 w-12',
  '56px': 'h-14 w-14',
  '60px': 'h-[60px] w-[60px]',
  '70px': 'h-[70px] w-[70px]',
  '100px': 'h-[100px] w-[100px]',
  '128px': 'h-32 w-32',
};

// Font size mapping based on avatar size
const fontSizeMap: Record<string, string> = {
  '24px': 'text-xs',
  '32px': 'text-sm',
  '40px': 'text-base',
  '44px': 'text-base',
  '48px': 'text-lg',
  '56px': 'text-xl',
  '60px': 'text-2xl',
  '70px': 'text-3xl',
  '100px': 'text-4xl',
  '128px': 'text-5xl',
};

export const TeeAvatar = React.forwardRef<HTMLDivElement, TeeAvatarProps>(
  (
    {
      size = '40px',
      style = 'Text',
      type = 'Profile-1',
      initials = 'AB',
      src,
      alt = 'Avatar',
      className,
      contact,
    },
    ref
  ) => {
    const [imgFailed, setImgFailed] = useState(false);

    // Reset error state whenever the image source changes
    useEffect(() => {
      setImgFailed(false);
    }, [src]);

    const bgColor = colorVariants[type] || colorVariants['Default'];
    const textColor = textColorVariants[type] || textColorVariants['Default'];
    const sizeClass = sizeMap[size] || sizeMap['40px'];
    const fontSizeClass = fontSizeMap[size] || fontSizeMap['40px'];
    const initialsToUse = contact
      ? `${contact.firstName[0]}${contact.lastName[0]}`
      : initials;

    // Image style — falls back to initials if the image fails to load
    if (style === 'Image' && src && !imgFailed) {
      return (
        <div
          ref={ref}
          className={cn(
            'relative flex items-center justify-center overflow-hidden rounded-full flex-shrink-0',
            sizeClass,
            className
          )}
        >
          <img
            src={src}
            alt={alt}
            className="h-full w-full object-cover"
            onError={() => setImgFailed(true)}
          />
        </div>
      );
    }

    // Empty state style
    if (style === 'Empty State') {
      return (
        <div
          ref={ref}
          className={cn(
            'relative flex items-center justify-center rounded-full flex-shrink-0',
            sizeClass,
            bgColor,
            className
          )}
        >
          <svg
            className="h-1/2 w-1/2"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 12C13.6569 12 15 10.6569 15 9C15 7.34315 13.6569 6 12 6C10.3431 6 9 7.34315 9 9C9 10.6569 10.3431 12 12 12Z"
              fill="white"
            />
            <path
              d="M12 14C9.33579 14 4 15.3431 4 18V20C4 20.5523 4.44772 21 5 21H19C19.5523 21 20 20.5523 20 20V18C20 15.3431 14.6642 14 12 14Z"
              fill="white"
            />
          </svg>
        </div>
      );
    }

    // Text style (default)
    return (
      <div
        ref={ref}
        className={cn(
          'relative flex items-center justify-center rounded-full flex-shrink-0 font-semibold',
          sizeClass,
          bgColor,
          textColor,
          fontSizeClass,
          className
        )}
      >
        {initialsToUse}
      </div>
    );
  }
);

TeeAvatar.displayName = 'TeeAvatar';

export { TeeAvatar as default };
